import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

export interface SeoSettings {
  site_name: string;
  site_tagline: string;
  default_title: string;
  default_description: string;
  default_og_image: string;
  twitter_handle: string;
}

export interface OrganizationSettings {
  name: string;
  legal_name: string;
  url: string;
  logo_url: string;
  founding_date: string;
  description: string;
  contact_email: string;
  social_links: {
    twitter: string;
    linkedin: string;
    github: string;
  };
}

export interface PageSeo {
  id: string;
  path: string;
  title: string | null;
  description: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  canonical_url: string | null;
  no_index: boolean;
  json_ld: Json | null;
  display_order: number;
  include_in_sitemap: boolean;
  priority: number;
  change_freq: string;
  created_at: string;
  updated_at: string;
}

export function useSiteSettings() {
  return useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*');
      
      if (error) throw error;
      
      const settings: Record<string, unknown> = {};
      data?.forEach((row: { key: string; value: Json }) => {
        settings[row.key] = row.value;
      });
      
      return {
        seo: settings.seo as SeoSettings,
        organization: settings.organization as OrganizationSettings,
      };
    },
  });
}

export function useUpdateSiteSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: Json }) => {
      const { error } = await supabase
        .from('site_settings')
        .update({ value })
        .eq('key', key);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
    },
  });
}

export function usePageSeo(path?: string) {
  return useQuery({
    queryKey: ['page-seo', path],
    queryFn: async () => {
      if (path) {
        const { data, error } = await supabase
          .from('page_seo')
          .select('*')
          .eq('path', path)
          .maybeSingle();
        
        if (error) throw error;
        return data as PageSeo | null;
      }
      return null;
    },
    enabled: !!path,
  });
}

export function useAllPageSeo() {
  return useQuery({
    queryKey: ['page-seo'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('page_seo')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data as PageSeo[];
    },
  });
}

export function useCreatePageSeo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (pageSeo: {
      path: string;
      title?: string | null;
      description?: string | null;
      og_title?: string | null;
      og_description?: string | null;
      og_image?: string | null;
      canonical_url?: string | null;
      no_index?: boolean;
      json_ld?: Json | null;
      display_order?: number;
      include_in_sitemap?: boolean;
      priority?: number;
      change_freq?: string;
    }) => {
      const { error } = await supabase
        .from('page_seo')
        .insert([pageSeo]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-seo'] });
    },
  });
}

export function useUpdatePageSeo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<{
      path: string;
      title: string | null;
      description: string | null;
      og_title: string | null;
      og_description: string | null;
      og_image: string | null;
      canonical_url: string | null;
      no_index: boolean;
      json_ld: Json | null;
      display_order: number;
      include_in_sitemap: boolean;
      priority: number;
      change_freq: string;
    }>) => {
      const { error } = await supabase
        .from('page_seo')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-seo'] });
    },
  });
}

export function useDeletePageSeo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('page_seo')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-seo'] });
    },
  });
}
